import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';

import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { useSearchParams } from 'react-router-dom';
import moment from 'moment/moment';
import CustomDashboardGrid, { getNewWidgetPosition } from './customDashboardGrid';
import { PageWrapper, PopupTypes } from '../components/partials';
import ResizeSensor from '../components/partials/resize_sensor';
import { useGridResize, useLocalStorageState } from '../utils/customHooks';
import {
    CLONE_DASHBOARD,
    CREATE_DASHBOARD,
    DELETE_DASHBOARD,
    READ_DASHBOARDS,
    TOGGLE_FAVORITE_DASHBOARD,
    UPDATE_DASHBOARD,
} from './queries';
import { CircularPlusButton, HelpMenu, ThreeLoadingDots, Toggle } from '../components/widgets';
import AutoCompleteField from '../components/molecules/AutoCompleteField';
import {
    dashboardPageName,
    getUniqueName,
    last7Days,
    trackDashboardsMixpanelEvent,
    trackDashboardsWidgetOpenedMixpanelEvent,
    trackDashboardsWidgetUpdateMixpanelEvent,
    WIDGET_CHANGE_TYPES,
} from './utils';
import EditableTitle from '../components/widgets/EditableTitle';
import css from './dashboard.css';
import { uuidv4 } from '../governance/utils';
import {
    useFetchAllFields,
    useFetchCreativesAIDimensionsFields,
    useFetchDisableReports,
    useFetchFilterFields,
} from './hooks';
import Show from '../resources/svg/eye_open.svg';
import Hide from '../resources/svg/eye_close.svg';
import Tooltip from '../components/molecules/Tooltip';
import GeneralPopup from '../components/organisms/GeneralPopup';
import EmptyState from '../components/partials/EmptyState/EmptyState';
import { getEmptyStateConfig } from '../reducers/reportsConfig/baseReportConfig';
import { STATIC } from '../../../dashboard/js/locale/en';
import { getTimePeriodByDates } from '../utils/regional';
import Icon, { IconSizes } from '../components/foundations/Icon';
import { validateName } from '../components/utilities/Sanitize';
import { splitCreativesAIDimensions } from '../utils/reports';
import DashboardOption from './DashboardOption';

const NEW_DASHBOARD_TITLE = 'New Dashboard';
const FAQ_LINK = 'https://support.singular.net/hc/en-us/articles/206241026-Dashboard-FAQ';
const SELECT_DASHBOARD_LABEL = 'STATIC.PAGES.DASHBOARD.SELECT_DASHBOARD';

const ITEM_TYPES = {
    DASHBOARD: 'dashboard',
    WIDGET: 'widget',
};

const LOCAL_STORAGE_KEY = 'Dashboard.active_dashboard';

function Dashboard({
    translate,
    emptyStateOrgData = {},
    dateLimit,
    onCloseReportsEmptyState = () => {},
    preconfiguredType,
    includeImpersonated = false,
    readOnly = false,
}) {
    const { loading, error, data, refetch } = useQuery(READ_DASHBOARDS, {
        variables: { preconfiguredType, includeImpersonated },
    });
    const [searchParams, setSearchParams] = useSearchParams();

    const dashboards = useMemo(() => {
        if (loading || error || !data || !data.dashboards) {
            return [];
        }

        return sortBy(data.dashboards, [dashboard => !dashboard.isFavorite, dashboard => dashboard.created]);
    }, [loading, error, data]);

    const localStoragePath = preconfiguredType ? `${LOCAL_STORAGE_KEY}.${preconfiguredType}` : LOCAL_STORAGE_KEY;

    const [activeDashboard, setActiveDashboard] = useLocalStorageState(localStoragePath, null, {
        serialize: dashboard => (dashboard?.id ? JSON.stringify(dashboard.id) : null),
        deserialize: () => null,
    });

    const [apiFields, setApiFields] = useState(null);

    const { value: fieldsResponse } = useFetchAllFields();
    const { value: filterFieldsResponse } = useFetchFilterFields();
    const { value: creativesAIDimensionsResponse } = useFetchCreativesAIDimensionsFields();

    useEffect(() => {
        if (fieldsResponse && filterFieldsResponse) {
            setApiFields({
                ...fieldsResponse,
                ...splitCreativesAIDimensions(creativesAIDimensionsResponse || []),
                filters: filterFieldsResponse,
            });
        }
    }, [fieldsResponse, filterFieldsResponse, creativesAIDimensionsResponse]);

    const parsedRouting = useMemo(() => {
        const activeDashboardId = preconfiguredType ? activeDashboard?.id : searchParams.get('active-dashboard');

        return {
            // unescape url Param ('%3d' -> '='), but keep +, because it's a legit base64 character
            navigatedDashboardId: activeDashboardId ? unescape(activeDashboardId).replaceAll(' ', '+') : null,
            createNew: searchParams.get('new') === 'true',
            useDefault: searchParams.get('default') === 'true',
        };
    }, [searchParams, preconfiguredType, activeDashboard]);

    const navigateToDashboard = newDashboard => {
        if (!newDashboard) {
            return;
        }

        setActiveDashboard(newDashboard);
        !preconfiguredType && setSearchParams({ 'active-dashboard': escape(newDashboard.id).replace('/', '%2F') });
    };

    const getDashboardFromRoute = () => dashboards.find(({ id }) => id === parsedRouting.navigatedDashboardId);

    const getDashboardFromLocalStorage = () => {
        const savedId = window.localStorage.getItem(localStoragePath);
        const parsedId = (() => {
            try {
                return JSON.parse(savedId);
            } catch {
                return null;
            }
        })();

        return parsedId && dashboards.find(dashboard => dashboard.id === parsedId);
    };

    useEffect(() => {
        if (!dashboards.length) return;

        const firstDashboard = dashboards[0];

        if (preconfiguredType) {
            if (!activeDashboard || activeDashboard.id !== firstDashboard.id) setActiveDashboard(firstDashboard);
        } else {
            const dashboardToNavigate = getDashboardFromRoute() || getDashboardFromLocalStorage() || firstDashboard;

            if (!activeDashboard || activeDashboard.id !== dashboardToNavigate.id)
                navigateToDashboard(dashboardToNavigate);
        }
    }, [dashboards]);

    useEffect(() => {
        if (!activeDashboard || !dashboards.length) return;

        trackDashboardsMixpanelEvent('Viewed Dashboard', {
            dashboard_name: activeDashboard.name,
            is_default: activeDashboard.id === dashboards[0]?.id,
            preconfiguredType,
        });
    }, [activeDashboard]);

    const onNewDashboardCreated = (newDashboard, isCloned) => {
        trackDashboardsMixpanelEvent('New Dashboard Created', {
            dashboard_name: newDashboard.name,
            is_cloned: isCloned,
        });

        refetch();
        navigateToDashboard(newDashboard);
    };

    // Prevent multiple clicks from creating the same Dashboard
    const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
    const [createDashboard] = useMutation(CREATE_DASHBOARD, {
        onCompleted({ createDashboard: newDashboard }) {
            onNewDashboardCreated(newDashboard, false);
            setIsCreatingDashboard(false);
        },
    });

    const getDashboardNameError = useCallback(() => {
        if (!activeDashboard) return '';

        const dashboardName = activeDashboard.name;

        if (!dashboardName) {
            return translate('STATIC.PAGES.DASHBOARD.ERRORS.MUST_NOT_BE_EMPTY');
        }

        if (!validateName(dashboardName)) {
            return translate('STATIC.PAGES.DASHBOARD.ERRORS.INVALID_DASHBOARD_NAME');
        }

        if (dashboards.some(d => d.id !== activeDashboard.id && d.name === dashboardName)) {
            return translate('STATIC.PAGES.DASHBOARD.ERRORS.DASHBOARD_ALREADY_EXISTS');
        }

        return '';
    }, [activeDashboard, dashboards]);

    // Memoize the dashboard widgets based on the retrieved dashboard object
    const widgets = useMemo(() => {
        if (!activeDashboard) {
            return [];
        }

        return activeDashboard.widgets.map(widget => ({
            ...widget,
            options: {
                ...widget.options,
                rows: widget.options.rows.filter(x => widget.query.dimensions.includes(x)),
                columns: widget.options.columns.filter(x => widget.query.dimensions.includes(x)),
            },
        }));
    }, [activeDashboard]);

    const [cloneDashboard] = useMutation(CLONE_DASHBOARD, {
        onCompleted({ cloneDashboard: newDashboard }) {
            onNewDashboardCreated(newDashboard, true);
        },
    });

    const [deleteDashboard] = useMutation(DELETE_DASHBOARD, {
        onCompleted(data, { variables }) {
            const deletedId = variables.id;
            const deletedDashboard = dashboards.find(d => d.id === deletedId);

            trackDashboardsMixpanelEvent('Deleted Dashboard', {
                dashboard_name: deletedDashboard?.name,
            });

            refetch();

            if (dashboards.length <= 1 || activeDashboard?.id !== deletedId) return;

            const oldIndex = dashboards.findIndex(d => d.id === deletedId);
            const newIndex = oldIndex === 0 ? 1 : oldIndex - 1;
            navigateToDashboard(dashboards[newIndex]);
        },
    });

    // noinspection JSUnusedLocalSymbols
    // eslint-disable-next-line no-unused-vars
    const [updatingGraphQL, setUpdatingGraphQL] = useState(false);
    const [updateDashboard] = useMutation(UPDATE_DASHBOARD, {
        update: () => {
            refetch();
            setUpdatingGraphQL(false);
        },
    });

    const [toggleFavoriteDashboard] = useMutation(TOGGLE_FAVORITE_DASHBOARD);

    const apolloClient = useApolloClient();

    const handleToggleFavorite = useCallback(
        dashboardId => {
            const cachedData = apolloClient.readQuery({
                query: READ_DASHBOARDS,
                variables: { preconfiguredType, includeImpersonated },
            });

            if (cachedData?.dashboards) {
                const updatedDashboards = cachedData.dashboards.map(cachedDashboard =>
                    cachedDashboard.id === dashboardId
                        ? { ...cachedDashboard, isFavorite: !cachedDashboard.isFavorite }
                        : cachedDashboard
                );

                apolloClient.writeQuery({
                    query: READ_DASHBOARDS,
                    variables: { preconfiguredType, includeImpersonated },
                    data: { dashboards: updatedDashboards },
                });
            }

            toggleFavoriteDashboard({
                variables: { dashboardId },
            });
        },
        [apolloClient, preconfiguredType, toggleFavoriteDashboard]
    );

    const debouncedUpdateDashboard = useMemo(() => debounce(updateDashboard, 100), [updateDashboard]);

    // Callback on grid changes, persists back to server
    const onUpdateWidgets = useCallback(
        (newWidgets, widget, changeType) => {
            if (isEqual(widgets, newWidgets)) {
                return;
            }

            setUpdatingGraphQL(true);
            const newDashboard = { ...activeDashboard, widgets: newWidgets };
            setActiveDashboard(newDashboard);
            debouncedUpdateDashboard({ variables: newDashboard });
            if (changeType !== WIDGET_CHANGE_TYPES.LAYOUT) {
                trackDashboardsWidgetUpdateMixpanelEvent({
                    dashboard: activeDashboard,
                    widget,
                    changeType,
                });
            }
            if (changeType === WIDGET_CHANGE_TYPES.ADDED || changeType === WIDGET_CHANGE_TYPES.EDITED) {
                trackDashboardsWidgetOpenedMixpanelEvent({
                    dashboard: activeDashboard,
                    widget,
                    changeType,
                    isSaved: true,
                });
            }
        },
        [activeDashboard]
    );

    const [width, onGridResize] = useGridResize();

    const setDashboardName = useCallback(
        name => {
            const newDashboard = { ...activeDashboard, name };
            setActiveDashboard(newDashboard);
        },
        [activeDashboard]
    );

    const updateDashboardName = useCallback(() => {
        if (!getDashboardNameError()) {
            setUpdatingGraphQL(true);
            debouncedUpdateDashboard({ variables: activeDashboard });
        }
    }, [activeDashboard]);

    const [deletedItem, setDeletedItem] = useState(null);

    const { disable_report_state: disableReportState = null } = useFetchDisableReports();

    // Private / Public Toggle State
    const [toggleText, setToggleText] = useState(STATIC.PAGES.DASHBOARD.PUBLIC_DASHBOARD);
    const [toolTipText, setToolTipText] = useState('STATIC.PAGES.DASHBOARD.ACTIONS.MAKE_PRIVATE');
    const shared = activeDashboard && activeDashboard.shared;
    const isPreconfigured = activeDashboard && activeDashboard.isPreconfigured;
    const isCreator = activeDashboard && activeDashboard.isCreator;
    const [changeToggle, setChangeToggle] = useState(false);

    const changePublicPrivate = () => {
        const newDashboard = { ...activeDashboard, shared: !activeDashboard.shared };
        setActiveDashboard(newDashboard);
        debouncedUpdateDashboard({ variables: newDashboard });
        setChangeToggle(false);
        const eventName = newDashboard.shared ? 'Clicked Public' : 'Clicked Private';
        trackDashboardsMixpanelEvent(eventName, {
            dashboard_name: newDashboard.name,
        });
    };

    // Date Picker Changed
    const changeDatePicker = useCallback(
        (startDate, endDate) => {
            // We need this because some of our clients use other languages
            // and we want to save the time period in english in our backend
            const timePeriodInEnglish = getTimePeriodByDates(startDate, endDate);
            const newDashboard = {
                ...activeDashboard,
                startDate,
                endDate,
                timePeriod: timePeriodInEnglish,
            };
            setActiveDashboard(newDashboard);
            debouncedUpdateDashboard({ variables: newDashboard });
        },
        [activeDashboard]
    );

    useEffect(() => {
        if (!activeDashboard) return;

        setToggleText(shared ? STATIC.PAGES.DASHBOARD.PUBLIC_DASHBOARD : STATIC.PAGES.DASHBOARD.PRIVATE_DASHBOARD);
        if (shared) {
            setToolTipText(
                isCreator
                    ? 'STATIC.PAGES.DASHBOARD.ACTIONS.MAKE_PRIVATE'
                    : 'STATIC.PAGES.DASHBOARD.ACTIONS.DISABLED_TOOLTIP'
            );
        } else {
            setToolTipText('STATIC.PAGES.DASHBOARD.ACTIONS.MAKE_PUBLIC');
        }
    }, [activeDashboard]);

    const emptyStateDashboardData = getEmptyStateConfig(dashboardPageName);
    const activeDashboardName = activeDashboard?.name || '';

    const dashboardOptions = useMemo(
        () => dashboards.map(dashboard => ({ value: dashboard.id, label: dashboard.name })),
        [dashboards]
    );

    const activeDashboardValue = useMemo(
        () => (activeDashboard ? [{ label: activeDashboard.name, value: activeDashboard.id }] : []),
        [activeDashboard]
    );

    const renderDashboardOption = useCallback(
        option => {
            const dashboard = dashboards.find(d => d.id === option.value);
            return (
                <DashboardOption
                    isFavorite={dashboard?.isFavorite}
                    dashboardId={dashboard?.id}
                    dashboardName={dashboard?.name}
                    creatorEmail={dashboard?.creatorEmail}
                    isShared={dashboard?.shared}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={() =>
                        setDeletedItem({ id: dashboard.id, type: ITEM_TYPES.DASHBOARD, name: dashboard.name })
                    }
                />
            );
        },
        [dashboards, handleToggleFavorite]
    );

    return (
        <div>
            {!preconfiguredType && (
                <div className={classNames(css.dashboardHeaderContainer, css.dashboardHeader)}>
                    <div className={css.dashboardHeader}>
                        <div className={css.dashboardSelection}>
                            <AutoCompleteField
                                advanced
                                innerContainerClass={css.dashboardInput}
                                label={SELECT_DASHBOARD_LABEL}
                                options={dashboardOptions}
                                value={activeDashboardValue}
                                onChange={option => navigateToDashboard(dashboards.find(x => x.id === option.value))}
                                isMulti={false}
                                renderOption={renderDashboardOption}
                            />
                        </div>
                        {!readOnly && (
                            <div className={css.actionButtons}>
                                <Tooltip interactive titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.NEW_DASHBOARD">
                                    <CircularPlusButton
                                        disabled={!!disableReportState || isCreatingDashboard}
                                        onClick={() => {
                                            setIsCreatingDashboard(true);
                                            createDashboard({
                                                variables: {
                                                    name: getUniqueName(dashboards, NEW_DASHBOARD_TITLE),
                                                    shared: true,
                                                    startDate: last7Days,
                                                    endDate: moment().format('YYYY-MM-DD'),
                                                },
                                            });
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip interactive titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.CLONE">
                                    <Icon
                                        name="copy"
                                        size={IconSizes.MEDIUM}
                                        className={css.copyDashboardIcon}
                                        onClick={() => {
                                            cloneDashboard({
                                                variables: {
                                                    id: activeDashboard.id,
                                                    name: getUniqueName(dashboards, activeDashboard.name),
                                                    shared: true,
                                                    startDate: activeDashboard.startDate,
                                                    endDate: activeDashboard.endDate,
                                                    timePeriod: activeDashboard.timePeriod,
                                                },
                                            });
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip interactive titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.DELETE">
                                    <Icon
                                        name="delete-2-line"
                                        size={IconSizes.MEDIUM}
                                        className={css.copyDashboardIcon}
                                        onClick={() =>
                                            setDeletedItem({
                                                id: activeDashboard.id,
                                                type: ITEM_TYPES.DASHBOARD,
                                                name: activeDashboard.name,
                                            })
                                        }
                                    />
                                </Tooltip>

                                {!isPreconfigured && (
                                    <div className={css.togglePrivatePublic}>
                                        <Tooltip interactive titleTranslationKey={toolTipText}>
                                            <Toggle
                                                disabled={!isCreator}
                                                className={css.toggleButton}
                                                onToggle={() => setChangeToggle(true)}
                                                onChange={() => setChangeToggle(true)}
                                                checked={shared}
                                            />
                                        </Tooltip>
                                        {shared ? (
                                            <Show
                                                className={classNames(css.eyeButton, {
                                                    [css.eyeButtonDisabled]: !isCreator,
                                                })}
                                                onClick={() => {
                                                    if (isCreator) {
                                                        setChangeToggle(true);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Hide className={css.eyeButton} onClick={() => setChangeToggle(true)} />
                                        )}
                                        <div className={css.toggleText}>{toggleText}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <HelpMenu faqLink={FAQ_LINK} />
                </div>
            )}
            <PageWrapper>
                {!loading &&
                    (isPreconfigured ? (
                        <div className={classNames(css.titleContainer, css.preconfiguredTitle)}>
                            {activeDashboardName}
                        </div>
                    ) : (
                        <div className={css.titleContainer}>
                            <EditableTitle
                                placeholder={translate('STATIC.PAGE_HEADERS.DASHBOARD')}
                                value={activeDashboardName}
                                onTextChanged={setDashboardName}
                                onTextChangeComplete={updateDashboardName}
                                error={getDashboardNameError()}
                            />
                        </div>
                    ))}
                {emptyStateOrgData && emptyStateOrgData.show_empty_state_reports && (
                    <div className={css.emptyState}>
                        <EmptyState
                            titleText={emptyStateDashboardData.titleText}
                            subTitleText={emptyStateDashboardData.subTitleText}
                            tasksTodo={emptyStateDashboardData.tasks}
                            tasksDone={emptyStateOrgData.tasks}
                            showCloseIcon={emptyStateDashboardData.tasks.some(
                                task => emptyStateOrgData.tasks[task.topic]
                            )}
                            onCloseEmptyState={onCloseReportsEmptyState}
                            pageName="STATIC.PAGE_HEADERS.DASHBOARD"
                        />
                    </div>
                )}

                <ResizeSensor onResize={onGridResize} />
                {loading && (
                    <div>
                        Loading
                        <ThreeLoadingDots />
                    </div>
                )}

                <GeneralPopup
                    open={!!deletedItem}
                    type={PopupTypes.DELETE}
                    title={
                        !deletedItem ? '' : `STATIC.PAGES.DASHBOARD.DELETE_${deletedItem.type.toUpperCase()}_WARNING`
                    }
                    titleTextReplace={
                        deletedItem?.type === ITEM_TYPES.DASHBOARD && deletedItem?.name
                            ? { dashboardName: deletedItem.name }
                            : {}
                    }
                    text="STATIC.PAGES.DASHBOARD.DELETE_IS_PERMANENT_WARNING"
                    acceptText="STATIC.PAGES.DASHBOARD.ACTIONS.DELETE_CONFIRM"
                    rejectText="STATIC.PAGES.ALERTS_V2.DELETE_MODAL.REJECT_BUTTON_TEXT"
                    onAccept={useCallback(() => {
                        const { id, type } = deletedItem;
                        setDeletedItem(null);

                        if (type === ITEM_TYPES.DASHBOARD) {
                            deleteDashboard({ variables: { id } });
                            return;
                        }

                        const filteredWidgets = widgets.filter(x => x.gridParams.i !== id);
                        onUpdateWidgets(
                            filteredWidgets,
                            widgets.find(w => w.gridParams.i === id),
                            WIDGET_CHANGE_TYPES.DELETED
                        );
                    }, [deletedItem])}
                    onReject={useCallback(() => setDeletedItem(null), [])}
                    showSpinner={false}
                />

                <GeneralPopup
                    open={changeToggle}
                    type={PopupTypes.INFO_WITH_CANCEL}
                    title={`STATIC.PAGES.DASHBOARD.CHANGE_TO_${shared ? 'PRIVATE' : 'PUBLIC'}_TITLE`}
                    text={`STATIC.PAGES.DASHBOARD.CHANGE_TO_${shared ? 'PRIVATE' : 'PUBLIC'}_SUB_TITLE`}
                    acceptText={`STATIC.PAGES.DASHBOARD.MAKE_${shared ? 'PRIVATE' : 'PUBLIC'}`}
                    rejectText="STATIC.PAGES.ALERTS_V2.DELETE_MODAL.REJECT_BUTTON_TEXT"
                    onAccept={changePublicPrivate}
                    onReject={() => {
                        setChangeToggle(false);
                    }}
                    showSpinner={false}
                />

                {error && <div>Error loading dashboard...</div>}
                {activeDashboard && (
                    <CustomDashboardGrid
                        key={activeDashboard.id}
                        dashboard={activeDashboard}
                        widgets={widgets}
                        updateWidgets={onUpdateWidgets}
                        cloneWidget={i => {
                            const clonedWidget = cloneDeep(widgets.find(x => x.gridParams.i === i));
                            clonedWidget.name = getUniqueName(widgets, clonedWidget.name);
                            clonedWidget.gridParams.i = `new.widget.${uuidv4()}`;

                            const [x, y] = getNewWidgetPosition(widgets, clonedWidget);
                            clonedWidget.gridParams.x = x;
                            clonedWidget.gridParams.y = y;

                            onUpdateWidgets([...widgets, clonedWidget], clonedWidget, WIDGET_CHANGE_TYPES.CLONED);
                        }}
                        deleteWidget={i => setDeletedItem({ id: i, type: ITEM_TYPES.WIDGET })}
                        apiFields={apiFields}
                        width={width}
                        disableReportState={!!disableReportState}
                        updatingGraphQL={updatingGraphQL}
                        dateLimit={dateLimit}
                        updateDate={changeDatePicker}
                        preconfiguredType={preconfiguredType}
                        readOnly={readOnly}
                        resizable={!readOnly}
                    />
                )}
            </PageWrapper>
        </div>
    );
}

Dashboard.propTypes = {
    translate: PropTypes.func.isRequired,
    emptyStateOrgData: PropTypes.objectOf(PropTypes.any),
    dateLimit: PropTypes.objectOf(PropTypes.any),
    onCloseReportsEmptyState: PropTypes.func,
    preconfiguredType: PropTypes.string,
    includeImpersonated: PropTypes.bool,
    readOnly: PropTypes.bool,
};

export default withLocalize(Dashboard);
